import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CustomAutocomplete from 'components/shared/CustomAutocomplete/CustomAutocomplete';
import CustomDatePicker from 'components/shared/CustomDatePicker/CustomDatePicker';
import CustomTextField from 'components/shared/CustomTextField/CustomTextField';
import dayjs, { Dayjs } from 'dayjs';
import { useFormikContext } from 'formik';
import { FieldConfig, PIPELINE_MAP } from 'model/deals.model';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DealsService } from 'service/deals.service';
import zeplinColor from 'theme/Color';
import {
  OPPORTUNITY_INFO_SECTION,
  SHARED_TOP_FIELDS,
  StaticDealFormFields,
} from 'utilities/constants';

interface BaseDealFormProps {
  fields: FieldConfig[];
}

interface SharedDropdownOptions {
  dealStructure?: string[];
  source?: string[];
  outcome?: string[];
  lossReason?: string[];
  passReason?: string[];
}

const baseFields = (fields: FieldConfig[], sharedDropdowns: SharedDropdownOptions = {}) => {
  const updatedStaticFields = StaticDealFormFields.map((field) => {
    if (field.type === 'select') {
      let options: string[] = [];

      switch (field.name) {
        case 'dealDetails.dealStructure':
          options = sharedDropdowns.dealStructure
            ? [...sharedDropdowns.dealStructure]
            : field.options
              ? Array.from(field.options)
              : [];
          break;
        case 'dealDetails.source':
          options = sharedDropdowns.source
            ? [...sharedDropdowns.source]
            : field.options
              ? Array.from(field.options)
              : [];
          break;
        case 'dealDetails.outcome':
          options = sharedDropdowns.outcome
            ? [...sharedDropdowns.outcome]
            : field.options
              ? Array.from(field.options)
              : [];
          break;
        case 'dealDetails.lossReason':
          options = sharedDropdowns.lossReason
            ? [...sharedDropdowns.lossReason]
            : field.options
              ? Array.from(field.options)
              : [];
          break;
        case 'dealDetails.passReason':
          options = sharedDropdowns.passReason
            ? [...sharedDropdowns.passReason]
            : field.options
              ? Array.from(field.options)
              : [];
          break;
        default:
          options = field.options ? Array.from(field.options) : [];
      }
      return {
        ...field,
        options,
      };
    }
    return field;
  });

  return [OPPORTUNITY_INFO_SECTION, ...SHARED_TOP_FIELDS, ...fields, ...updatedStaticFields];
};

export const BaseDealForm = ({ fields }: BaseDealFormProps) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xl'));
  const { values, handleChange, handleBlur, setFieldValue } = useFormikContext<any>();
  const details = values.dealDetails || {};
  const pipelineType = values.pipelineType || '';

  const [clientOptions, setClientOptions] = useState<{ Name: string; Id: string }[]>([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientTotalLength, setClientTotalLength] = useState(0);

  const [ownerOptions, setOwnerOptions] = useState<{ Name: string; Id: string }[]>([]);
  const [allOwnerOptions, setAllOwnerOptions] = useState<{ Name: string; Id: string }[]>([]);
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [ownerTotalLength, setOwnerTotalLength] = useState(0);

  const [sharedDropdowns, setSharedDropdowns] = useState<SharedDropdownOptions>({});

  const getDealTypeKey = useCallback((pipelineType: string): string | null => {
    if (!pipelineType) return null;

    const apiKey = Object.keys(PIPELINE_MAP).find((key) => PIPELINE_MAP[key] === pipelineType);

    return apiKey || null;
  }, []);

  const fetchSharedDropdowns = useCallback(async (dealTypeKey: string) => {
    try {
      const dropdownsData = await DealsService.getDealDropdowns(dealTypeKey);

      const options: SharedDropdownOptions = {};

      if (dropdownsData.dealStructure && Array.isArray(dropdownsData.dealStructure)) {
        options.dealStructure = [...dropdownsData.dealStructure];
      }
      if (dropdownsData.source && Array.isArray(dropdownsData.source)) {
        options.source = [...dropdownsData.source];
      }
      if (dropdownsData.outcome && Array.isArray(dropdownsData.outcome)) {
        options.outcome = [...dropdownsData.outcome];
      }
      if (dropdownsData.lossReason && Array.isArray(dropdownsData.lossReason)) {
        options.lossReason = [...dropdownsData.lossReason];
      }
      if (dropdownsData.passReason && Array.isArray(dropdownsData.passReason)) {
        options.passReason = [...dropdownsData.passReason];
      }
      setSharedDropdowns(options);
    } catch (error) {
      console.error('Failed to fetch shared dropdown values:', error);
      setSharedDropdowns({});
    }
  }, []);

  const formFields = useMemo(() => baseFields(fields, sharedDropdowns), [fields, sharedDropdowns]);

  const fetchClients = useCallback(async (searchText?: string) => {
    try {
      setClientLoading(true);
      const data = await DealsService.getAllClients(searchText);
      const clients = data?.clients || (Array.isArray(data) ? data : []);
      const formattedClients = clients
        .map((item: any) => ({
          Name: item.Name || item.name || item.clientName || '',
          Id: item.AccountId || item.Id || item.id || item.clientId || '',
        }))
        .filter((item: any) => item.Name && item.Id);
      setClientOptions(formattedClients);
      setClientTotalLength(formattedClients.length);
    } catch (error) {
      console.error('Failed to fetch clients', error);
      setClientOptions([]);
      setClientTotalLength(0);
    } finally {
      setClientLoading(false);
    }
  }, []);

  const fetchOwners = useCallback(async () => {
    try {
      setOwnerLoading(true);
      const data = await DealsService.getMembers();
      const members = data?.members || (Array.isArray(data) ? data : []);
      const formattedOwners = members
        .map((item: any) => ({
          Name: `${item.firstName} ${item.lastName}` || '',
          Id: item.email || '',
        }))
        .filter((item: any) => item.Name && item.Id);
      setAllOwnerOptions(formattedOwners);
      setOwnerOptions(formattedOwners);
      setOwnerTotalLength(formattedOwners.length);
    } catch (error) {
      console.error('Failed to fetch owners', error);
      setAllOwnerOptions([]);
      setOwnerOptions([]);
      setOwnerTotalLength(0);
    } finally {
      setOwnerLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
    fetchOwners();
  }, [fetchClients, fetchOwners]);

  useEffect(() => {
    if (pipelineType) {
      const dealTypeKey = getDealTypeKey(pipelineType);
      if (dealTypeKey) {
        fetchSharedDropdowns(dealTypeKey);
      }
    }
  }, [pipelineType, getDealTypeKey, fetchSharedDropdowns]);

  const handleClientSearch = useCallback(
    (_: React.SyntheticEvent, value: string) => {
      fetchClients(value);
    },
    [fetchClients],
  );

  const handleOwnerSearch = useCallback(
    (_: React.SyntheticEvent, value: string) => {
      if (!value) {
        setOwnerOptions(allOwnerOptions);
        return;
      }
      const filtered = allOwnerOptions.filter((owner) =>
        owner.Name.toLowerCase().includes(value.toLowerCase()),
      );
      setOwnerOptions(filtered);
    },
    [allOwnerOptions],
  );

  const renderField = (f: FieldConfig) => {
    const raw = details[f.name.replace('dealDetails.', '')];
    const value = raw ?? (f.type === 'number' ? '' : '');

    if (f.type === 'section') {
      return (
        <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
          {f.label}
        </Typography>
      );
    }

    if (f.type === 'checkbox') {
      return (
        <FormControlLabel
          label={f.label}
          control={
            <Checkbox
              name={f.name}
              checked={Boolean(raw)}
              onChange={(e) =>
                handleChange({ target: { name: f.name, value: e.target.checked } } as any)
              }
              onBlur={handleBlur}
            />
          }
        />
      );
    }

    if (f.type === 'date') {
      let dateValue: Dayjs | null = null;
      if (raw) {
        if (dayjs.isDayjs && dayjs.isDayjs(raw)) {
          dateValue = raw;
        } else if (raw instanceof Date) {
          dateValue = dayjs(raw);
        } else if (typeof raw === 'string') {
          dateValue = dayjs(raw);
        } else {
          dateValue = dayjs(raw);
        }
      }

      const dateSetter = ((newValue: Dayjs | null | ((prev: Dayjs | null) => Dayjs | null)) => {
        const actualValue = typeof newValue === 'function' ? newValue(dateValue) : newValue;
        const formattedValue = actualValue ? actualValue.toDate() : null;
        setFieldValue(f.name, formattedValue);
      }) as React.Dispatch<React.SetStateAction<Dayjs | null>>;

      return <CustomDatePicker value={dateValue} setValue={dateSetter} label={f.label} />;
    }

    if (f.type === 'autocomplete' && f.autocompleteType) {
      const fieldName = f.name.replace('dealDetails.', '');
      const currentValue = details[fieldName];

      const selectedOption =
        f.autocompleteType === 'client'
          ? clientOptions.find((opt) => opt.Name === currentValue || opt.Id === currentValue)
          : ownerOptions.find((opt) => opt.Name === currentValue || opt.Id === currentValue);

      const options = f.autocompleteType === 'client' ? clientOptions : ownerOptions;
      const loading = f.autocompleteType === 'client' ? clientLoading : ownerLoading;
      const totalLength = f.autocompleteType === 'client' ? clientTotalLength : ownerTotalLength;
      const baseHandleFilter =
        f.autocompleteType === 'client' ? handleClientSearch : handleOwnerSearch;

      const handleFilter = (event: React.SyntheticEvent, value: string) => {
        if (selectedOption && value !== selectedOption.Name) {
          setFieldValue(f.name, '');
          if (f.autocompleteType === 'client') {
            setFieldValue('dealDetails.accountId', '');
          }
        }
        baseHandleFilter(event, value);
      };

      return (
        <CustomAutocomplete
          popupIcon='/assets/Search.svg'
          required={f.required}
          options={options}
          label={f.label}
          getOptionLabel={(option: any) => option?.Name || ''}
          handleDeleteItems={() => {
            setFieldValue(f.name, '');
            if (f.autocompleteType === 'client') {
              setFieldValue('dealDetails.accountId', '');
            }
          }}
          selectedItems={{}}
          handleSelection={(option: any) => {
            setFieldValue(f.name, option?.Name || '');
            if (f.autocompleteType === 'client' && option?.Id) {
              setFieldValue('dealDetails.accountId', option.Id);
            }
          }}
          value={selectedOption || null}
          multiple={false}
          handleFilter={handleFilter}
          placeholder={f.label}
          currentLength={options.length}
          totalLength={totalLength}
          next={() => {}}
          loading={loading}
        />
      );
    }

    if (f.type === 'select' && f.options) {
      return (
        <FormControl fullWidth sx={{ width: '100%', minWidth: 0 }}>
          <InputLabel
            size={isXs ? 'small' : 'medium'}
            sx={{
              fontSize: isXs ? 14 : 16,
              '& .MuiFormLabel-asterisk': {
                color: zeplinColor.Error,
              },
            }}
            required={f.required}
          >
            {f.label}
          </InputLabel>
          <Select
            IconComponent={KeyboardArrowDown}
            required={f.required}
            name={f.name}
            value={value}
            label={f.label}
            onChange={handleChange}
            onBlur={handleBlur}
            size={isXs ? 'small' : 'medium'}
            sx={{
              width: '100%',
              minWidth: 0,
              '& .MuiSelect-select': {
                fontSize: isXs ? 14 : 16,
              },
            }}
          >
            {f.options.map((opt) => (
              <MenuItem key={String(opt)} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <CustomTextField
        required={f.required}
        style={{ width: '100%' }}
        name={f.name}
        label={f.label}
        type={f.type === 'number' ? 'number' : 'text'}
        text={value ?? ''}
        onChange={handleChange}
        onBlur={() => handleBlur(f.name as any)}
        multiline={f.multiline}
        form={true}
      />
    );
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2, width: '100%' }}>
      {formFields.map((f, i) => (
        <Box
          key={f.label + i}
          sx={{ gridColumn: { xs: 'span 12', sm: `span ${f.sm ?? f.xs ?? 12}` } }}
        >
          {renderField(f)}
        </Box>
      ))}
    </Box>
  );
};
